<!--
  TimePicker.vue

  A custom time picker styled to match the bespoke calendar picker used in the
  create/edit meal modals: a slate panel with green-filled selected cells and
  slate hover. Selectable hour + minute cells plus an AM/PM toggle.

  v-model is a 24-hour "HH:MM" string (e.g. "08:00"), or "" when unset — the same
  format the backend uses for meal-type times.
-->
<template>
  <div class="tp" ref="root">
    <!-- Trigger (input-like) -->
    <button type="button" class="tp-trigger" :class="{ 'tp-trigger--open': open }" @click="open = !open">
      <svg class="tp-clock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
        stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
      <span :class="display ? 'tp-value' : 'tp-placeholder'">{{ display || placeholder }}</span>
      <svg class="tp-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6" /></svg>
    </button>

    <!-- Popover -->
    <div v-if="open" class="tp-pop">
      <!-- AM / PM -->
      <div class="tp-meridiem">
        <button v-for="mer in ['AM', 'PM']" :key="mer" type="button" class="tp-mer-btn"
          :class="{ 'tp-mer-btn--active': meridiem === mer }" @click="setMeridiem(mer as 'AM' | 'PM')">
          {{ mer }}
        </button>
      </div>

      <div class="tp-section-label">Hour</div>
      <div class="tp-grid tp-grid--hours">
        <button v-for="h in hours" :key="h" type="button" class="tp-cell"
          :class="{ 'tp-cell--selected': hour12 === h }" @click="setHour(h)">{{ h }}</button>
      </div>

      <div class="tp-section-label">Minute</div>
      <div class="tp-grid tp-grid--minutes">
        <button v-for="m in minutes" :key="m" type="button" class="tp-cell"
          :class="{ 'tp-cell--selected': minute === m }" @click="setMinute(m)">{{ pad(m) }}</button>
      </div>

      <div class="tp-footer">
        <button type="button" class="tp-link" @click="clear">Clear</button>
        <button type="button" class="tp-done" @click="open = false">Done</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'

const props = withDefaults(defineProps<{ modelValue: string; placeholder?: string }>(), {
  placeholder: 'Select time',
})
const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]

const open = ref(false)
const root = ref<HTMLElement | null>(null)

// Working state, derived from the "HH:MM" model value.
const hour12 = ref<number | null>(null)
const minute = ref<number | null>(null)
const meridiem = ref<'AM' | 'PM'>('AM')

const pad = (n: number) => String(n).padStart(2, '0')

const parse = (value: string) => {
  if (!value || !/^\d{1,2}:\d{2}$/.test(value)) {
    hour12.value = null
    minute.value = null
    meridiem.value = 'AM'
    return
  }
  const [hStr, mStr] = value.split(':')
  const h = Number(hStr)
  const m = Number(mStr)
  meridiem.value = h < 12 ? 'AM' : 'PM'
  hour12.value = h % 12 === 0 ? 12 : h % 12
  minute.value = m
}

watch(() => props.modelValue, (v) => parse(v), { immediate: true })

// Human-readable trigger label, e.g. "8:30 AM".
const display = computed(() => {
  if (hour12.value === null || minute.value === null) return ''
  return `${hour12.value}:${pad(minute.value)} ${meridiem.value}`
})

const emitValue = () => {
  if (hour12.value === null) return
  const m = minute.value ?? 0
  const base = hour12.value % 12 // 12 -> 0
  const h24 = meridiem.value === 'PM' ? base + 12 : base
  emit('update:modelValue', `${pad(h24)}:${pad(m)}`)
}

const setHour = (h: number) => {
  hour12.value = h
  if (minute.value === null) minute.value = 0
  emitValue()
}
const setMinute = (m: number) => {
  minute.value = m
  if (hour12.value === null) hour12.value = 12
  emitValue()
}
const setMeridiem = (mer: 'AM' | 'PM') => {
  meridiem.value = mer
  emitValue()
}
const clear = () => {
  hour12.value = null
  minute.value = null
  meridiem.value = 'AM'
  emit('update:modelValue', '')
  open.value = false
}

// Close on outside click.
const onDocClick = (e: MouseEvent) => {
  if (open.value && root.value && !root.value.contains(e.target as Node)) open.value = false
}
onMounted(() => document.addEventListener('mousedown', onDocClick))
onBeforeUnmount(() => document.removeEventListener('mousedown', onDocClick))
</script>

<style scoped>
.tp {
  position: relative;
  font-family: 'Inter', sans-serif;
}

/* ── Trigger ── */
.tp-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 150px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #0f172a;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.tp-trigger:hover {
  border-color: #9ca3af;
}
.tp-trigger--open {
  border-color: #4f8646;
  box-shadow: 0 0 0 2px rgba(79, 134, 70, 0.2);
}
.tp-clock {
  width: 16px;
  height: 16px;
  color: #64748b;
  flex-shrink: 0;
}
.tp-value {
  flex: 1;
  text-align: left;
}
.tp-placeholder {
  flex: 1;
  text-align: left;
  color: #94a3b8;
}
.tp-caret {
  width: 14px;
  height: 14px;
  color: #94a3b8;
  flex-shrink: 0;
}

/* ── Popover (mirrors .calendar-wrap from the meal modal) ── */
.tp-pop {
  position: absolute;
  z-index: 50;
  top: calc(100% + 6px);
  left: 0;
  width: 248px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
}

.tp-meridiem {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
}
.tp-mer-btn {
  flex: 1;
  padding: 6px 0;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
}
.tp-mer-btn:hover {
  background: #f1f5f9;
}
.tp-mer-btn--active {
  background: #4f8646;
  border-color: #4f8646;
  color: #fff;
}

.tp-section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
  margin: 0 0 6px;
}

.tp-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  margin-bottom: 12px;
}
.tp-cell {
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 13px;
  color: #0f172a;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.tp-cell:hover {
  background: #e2e8f0;
}
.tp-cell--selected {
  background: #4f8646;
  color: #fff;
  font-weight: 600;
}
.tp-cell--selected:hover {
  background: #3f6e38;
}

.tp-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #e2e8f0;
  padding-top: 10px;
}
.tp-link {
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}
.tp-link:hover {
  color: #475569;
}
.tp-done {
  background: #4f8646;
  border: none;
  border-radius: 8px;
  color: #fff;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 16px;
}
.tp-done:hover {
  background: #3f6e38;
}
</style>
